import { collection, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { ApiKeySchema, type ApiKey } from '@etiquetachef/types';
import { firestore } from './firebase';
import { reportNonFatal } from './errorReport';

/**
 * Leitura das integrações da API pública (issue #263).
 *
 * Coleção root `api_keys`, gateada por `isSuperAdmin()` nas Rules. Ler não dá
 * acesso à API: o documento guarda apenas o sha256 do `client_secret`.
 *
 * Escrita é sempre por callable (`adminCreateApiKey`/`Update`/`Revoke`) — as
 * Rules negam escrita direta. Sem `orderBy` na query (ordena no cliente), como
 * nas demais coleções pequenas do admin, para não exigir índice.
 */
const TAG = 'admin/firestoreApiKeys';

/** Estado da tabela de integrações. */
export type ApiKeyListResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; apiKeys: ApiKey[] }
  | { kind: 'ERROR'; error: unknown };

/**
 * Assina a coleção root `api_keys` (todas as integrações da API pública).
 *
 * Origem: #263 — commit 89ca9fd6e, 2026-08-24.
 *
 * Contrato: `READY` com as integrações válidas por `ApiKeySchema`, ativas
 * primeiro e por nome; doc inválido é pulado com
 * `reportNonFatal('admin_api_key_unparseable')`; erro de snapshot vira `ERROR` (a tela mostra o alerta). Devolve
 * o unsubscribe.
 *
 * Usado por: `routes/ApiKeysPage.tsx`.
 *
 * Arquitetura: rule `match /api_keys/{keyId}` — leitura só `isSuperAdmin()`,
 * escrita negada (só as callables `adminCreateApiKey`/`adminUpdateApiKey`/
 * `adminRevokeApiKey`). Query sem filtro: não exige índice.
 *
 * Dados — `api_keys/{keyId}` (schema `ApiKeySchema`, `packages/types/src/publicApi.ts`):
 *   { "name": "parceiro-1 — produção", "partner": "parceiro-1",
 *     "clientId": "ec_parceiro-1_a1b2c3d4", "clientSecretHash": "<sha256 hex, 64>",
 *     "tenantScope": "PARTNER", "scopePartnerId": "partner-1", "revokedAt": null, … }
 * O segredo em claro nunca é gravado; ler este doc não dá acesso à API.
 *
 * Issues: #263 (API pública), #785 (escopo por parceiro — mudança de formato).
 */
export function subscribeApiKeys(onResult: (r: ApiKeyListResult) => void): Unsubscribe {
  const ref = collection(firestore, 'api_keys');
  return onSnapshot(
    ref,
    (snap) => {
      const apiKeys: ApiKey[] = [];
      for (const d of snap.docs) {
        const parsed = ApiKeySchema.safeParse({ id: d.id, ...d.data() });
        if (parsed.success) apiKeys.push(parsed.data);
        else {
          // Integração que some da tabela sem aviso é invisível para o
          // operador — e a mudança de formato da #785 é exatamente o tipo de
          // coisa que faria um doc deixar de passar no schema. Reporta além
          // do console (DevTools não chega até nós).
          console.warn(`${TAG}: integração ${d.id} ignorada`, parsed.error.issues);
          reportNonFatal('admin_api_key_unparseable', { keyId: d.id });
        }
      }
      // Ativas primeiro, revogadas no fim; dentro de cada grupo, por nome.
      apiKeys.sort((a, b) => {
        const aRevoked = a.revokedAt ? 1 : 0;
        const bRevoked = b.revokedAt ? 1 : 0;
        if (aRevoked !== bRevoked) return aRevoked - bRevoked;
        return a.name.localeCompare(b.name);
      });
      onResult({ kind: 'READY', apiKeys });
    },
    (err) => {
      console.error(`${TAG}: erro ao ler integrações`, err);
      onResult({ kind: 'ERROR', error: err });
    },
  );
}
